import { useEffect, useState } from "react";
import clsx from "clsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import {
  localNaiveInputToUtcIso,
  useTimezone,
  utcToLocalNaive,
} from "../lib/datetime";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
}

export function DiaryDrawer({ open, onClose }: DrawerProps) {
  const tz = useTimezone();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["diary"],
    queryFn: api.diary.list,
    enabled: open,
    refetchInterval: open ? 30_000 : undefined,
  });

  const [useNow, setUseNow] = useState(true);
  const [occurredInput, setOccurredInput] = useState("");
  const [description, setDescription] = useState("");
  // Reset is two-step: arm the confirm panel, then delete. `savedCsv`
  // tracks whether the operator took the CSV from inside that panel, so
  // the confirm button can keep nagging until they have a copy.
  const [confirmReset, setConfirmReset] = useState(false);
  const [savedCsv, setSavedCsv] = useState(false);

  // Default the time-of-occurrence input to "now in tz" so unchecking
  // the box gives a sensible starting point.
  useEffect(() => {
    if (open && occurredInput === "") {
      const naive = utcToLocalNaive(new Date(), tz);
      // Drop seconds + Z for the datetime-local input.
      if (naive) setOccurredInput(naive.replace(/Z$/, "").slice(0, 16));
    }
  }, [open, occurredInput, tz]);

  // Never leave the drawer armed for a wipe across open/close, and drop
  // the confirm if the diary empties out from under it (single deletes).
  useEffect(() => {
    if (!open || (list.data && list.data.entries.length === 0)) {
      setConfirmReset(false);
      setSavedCsv(false);
    }
  }, [open, list.data]);

  const create = useMutation({
    mutationFn: () => {
      const occurredAt = useNow
        ? null
        : localNaiveInputToUtcIso(occurredInput, tz);
      return api.diary.create(occurredAt, description);
    },
    onSuccess: () => {
      setDescription("");
      qc.invalidateQueries({ queryKey: ["diary"] });
    },
  });

  const del = useMutation({
    mutationFn: (id: number) => api.diary.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["diary"] }),
  });

  const reset = useMutation({
    mutationFn: () => api.diary.clear(),
    onSuccess: () => {
      setConfirmReset(false);
      setSavedCsv(false);
      qc.invalidateQueries({ queryKey: ["diary"] });
    },
  });

  const entryCount = list.data?.entries.length ?? 0;

  const canSubmit =
    description.trim().length > 0 &&
    (useNow || occurredInput.length > 0) &&
    !create.isPending;

  return (
    <>
      <div
        className={clsx(
          "fixed inset-0 bg-black/25 transition-opacity z-40",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={onClose}
      />
      <aside
        className={clsx(
          "fixed top-0 right-0 bottom-0 w-[480px] max-lg:w-full max-lg:max-w-none z-50 bg-surface1 border-l border-border1",
          "transform transition-transform duration-200",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-border1">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-dim">Tools</div>
            <h2 className="text-[15px] font-semibold text-text">Diary</h2>
          </div>
          <button onClick={onClose} className="text-muted hover:text-text text-lg">×</button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto h-[calc(100%-64px)]">
          <section className="space-y-3">
            <label className="flex items-center gap-2 text-[12px] text-text">
              <input
                type="checkbox"
                checked={useNow}
                onChange={(e) => setUseNow(e.target.checked)}
                className="accent-wb"
              />
              Use entry time
            </label>
            {!useNow && (
              <input
                type="datetime-local"
                value={occurredInput}
                onChange={(e) => setOccurredInput(e.target.value)}
                className="w-full bg-surface2 border border-border1 rounded px-3 py-2 text-text text-[13px] mono-nums focus:border-wb/60 outline-none"
              />
            )}
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What happened?"
              rows={3}
              className="w-full bg-surface2 border border-border1 rounded px-3 py-2 text-text text-[13px] focus:border-wb/60 outline-none resize-y"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => create.mutate()}
                disabled={!canSubmit}
                className={clsx(
                  "rounded px-3 py-2 text-[11px] uppercase tracking-widest transition-colors",
                  canSubmit
                    ? "bg-wb hover:bg-wbHot text-white"
                    : "bg-border1 text-dim cursor-not-allowed",
                )}
              >
                {create.isPending ? "Saving…" : "Add entry"}
              </button>
              <a
                href={api.diary.exportUrl()}
                className="ml-auto text-[11px] uppercase tracking-widest text-muted hover:text-text border border-border2 hover:border-wb rounded px-3 py-2"
              >
                Download CSV
              </a>
            </div>
            {create.isError && (
              <p className="text-[12px] text-wb">{(create.error as Error).message}</p>
            )}
          </section>

          <section>
            <div className="flex items-baseline gap-2 mb-2">
              <h3 className="text-[10.5px] uppercase tracking-[0.22em] text-dim">
                Entries
              </h3>
              {entryCount > 0 && (
                <span className="text-[10.5px] mono-nums text-dim">{entryCount}</span>
              )}
              <button
                onClick={() => setConfirmReset(true)}
                disabled={entryCount === 0 || confirmReset || reset.isPending}
                className={clsx(
                  "ml-auto text-[10.5px] uppercase tracking-wider transition-colors",
                  entryCount === 0 || confirmReset || reset.isPending
                    ? "text-dim cursor-not-allowed"
                    : "text-muted hover:text-wb",
                )}
              >
                Reset diary
              </button>
            </div>

            {confirmReset && (
              <div className="border border-wb/50 rounded p-3 mb-3 space-y-3">
                <p className="text-[12px] text-text">
                  Delete all {entryCount} {entryCount === 1 ? "entry" : "entries"}? This
                  cannot be undone.
                </p>
                <p className="text-[11.5px] text-muted">
                  {savedCsv
                    ? "CSV downloaded — check the file before you clear."
                    : "Download the CSV first if you need a record of the log."}
                </p>
                <div className="flex items-center gap-2">
                  <a
                    href={api.diary.exportUrl()}
                    onClick={() => setSavedCsv(true)}
                    className={clsx(
                      "text-[11px] uppercase tracking-widest rounded px-3 py-2 border transition-colors",
                      savedCsv
                        ? "text-muted border-border2 hover:text-text hover:border-wb"
                        : "text-text border-wb hover:bg-wb hover:text-white",
                    )}
                  >
                    {savedCsv ? "Download again" : "Download CSV"}
                  </a>
                  <button
                    onClick={() => {
                      setConfirmReset(false);
                      setSavedCsv(false);
                    }}
                    className="ml-auto text-[11px] uppercase tracking-widest text-muted hover:text-text border border-border2 hover:border-wb rounded px-3 py-2"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => reset.mutate()}
                    disabled={reset.isPending}
                    className={clsx(
                      "rounded px-3 py-2 text-[11px] uppercase tracking-widest transition-colors",
                      reset.isPending
                        ? "bg-border1 text-dim cursor-not-allowed"
                        : "bg-wb hover:bg-wbHot text-white",
                    )}
                  >
                    {reset.isPending
                      ? "Clearing…"
                      : savedCsv
                        ? "Clear diary"
                        : "Clear without saving"}
                  </button>
                </div>
                {reset.isError && (
                  <p className="text-[12px] text-wb">{(reset.error as Error).message}</p>
                )}
              </div>
            )}

            {list.data?.entries.length === 0 && (
              <p className="text-[12px] text-muted">No entries yet.</p>
            )}
            <ul className="space-y-2">
              {(list.data?.entries ?? []).map((e) => (
                <li
                  key={e.id}
                  className="border border-border1 rounded px-3 py-2 flex flex-col gap-1"
                >
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[12px] mono-nums text-text">
                      {fmtTs(e.occurredAt, tz)}
                    </span>
                    {e.chainageM != null && (
                      <span className="text-[11px] mono-nums text-muted">
                        ch {e.chainageM.toFixed(1)} m
                      </span>
                    )}
                    {fmtTsShort(e.enteredAt, tz) !== fmtTsShort(e.occurredAt, tz) && (
                      <span className="text-[10.5px] text-dim">
                        (logged {fmtTs(e.enteredAt, tz)})
                      </span>
                    )}
                    <button
                      onClick={() => del.mutate(e.id)}
                      className="ml-auto text-[10.5px] text-muted hover:text-wb uppercase tracking-wider"
                    >
                      Delete
                    </button>
                  </div>
                  <p className="text-[13px] text-text whitespace-pre-wrap">{e.description}</p>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </aside>
    </>
  );
}

function fmtTs(iso: string, tz: string): string {
  const naive = utcToLocalNaive(iso, tz);
  if (!naive) return iso;
  // "2026-05-06T12:35:00Z" -> "2026-05-06 12:35"
  return naive.replace("T", " ").replace(/Z$/, "").slice(0, 16);
}

function fmtTsShort(iso: string, tz: string): string {
  // Used for de-dup check: same minute granularity.
  return fmtTs(iso, tz);
}
